import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { generateTrackingID } from 'helpers'
import parseError from 'helpers/parseError'
import { getCorrelationID } from 'helpers/requestParams'
import { getAccountDetail } from 'helpers/user'
import qs from 'qs'
import {
  getAPIHost,
  getAccountDetailQuery,
  handleError
} from 'services/helpers'
import { FetchProps } from 'services/typings'
import { NetworkError } from 'typings/serverError'

const api = axios.create()

api.interceptors.request.use(requestConfig => {
  requestConfig.headers.set('X-SYF-Request-TrackingId', generateTrackingID())
  requestConfig.headers.set('X-SYF-Request-CorrelationId', getCorrelationID())
  return requestConfig
})

api.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: NetworkError) => {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw parseError(err)
  }
)

const fetchAsset = async <T>(
  requestConfig: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const apiHost = getAPIHost('ASSETS')
  const encodedParams = requestConfig.params

  /**
   * Note: when calling ASSETS we don't need to pass any headers
   */
  return axios({
    ...requestConfig,
    url: `${apiHost}${requestConfig.url}`,
    params: encodedParams
  })
}

const apiRequestor = async <T>({
  apiType,
  isBadRequestAllowed,
  moduleType,
  hasAccountInfo = true,
  urlencoded = false,
  requestConfig,
  permittedErrorCodes,
  noRedirect = false
}: FetchProps): Promise<AxiosResponse<T>> => {
  /*
   * Asset services responds with 403 error code when resource is not found.
   * This is a problem because the errorHandler method treats all 403 errors as
   * a session timeout. fetchAsset allows the error to be thrown and handled by
   * caller.
   */
  if (apiType === 'ASSETS') {
    return fetchAsset<T>(requestConfig)
  }

  try {
    const accountInfo = getAccountDetail(moduleType || '')
    const apiHost = getAPIHost(apiType)

    let body = requestConfig.data
    let encodedParams = requestConfig.params

    if (hasAccountInfo) {
      if (['GET', 'DELETE'].includes(requestConfig.method)) {
        const accountDetailQuery = getAccountDetailQuery(accountInfo)
        encodedParams = {
          ...encodedParams,
          ...accountDetailQuery
        }
      } else {
        /*
         * By default, axios serializes JavaScript objects to JSON
         * To send data in the application/x-www-form-urlencoded
         * we use the qs library
         * Reference: https://axios-http.com/docs/urlencoded
         */
        body = urlencoded
          ? qs.stringify({
              ...accountInfo,
              ...requestConfig.data
            })
          : {
              ...accountInfo,
              ...requestConfig.data
            }
      }
    }

    return await api({
      ...requestConfig,
      url: `${apiHost}${requestConfig.url}`,
      params: encodedParams,
      data: body
    })
  } catch (errorResponse) {
    handleError(
      errorResponse,
      isBadRequestAllowed,
      permittedErrorCodes,
      noRedirect
    )
    throw errorResponse
  }
}

export default apiRequestor
